import { useCallback, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveBackRoute } from '@/utils/backRoute';

/**
 * Key stamped onto each history entry of a page visit: the index of the entry
 * the visit started on. A page re-entered via history (an entry pushed by one
 * of its tab switches) keeps returning past all of its own tabs.
 */
const ENTRY_IDX_KEY = 'appBackEntryIdx';

type HistoryState = { idx?: unknown; [ENTRY_IDX_KEY]?: unknown } | null;

/**
 * React Router's BrowserRouter stamps every history entry with `idx` — 0 for
 * the entry the SPA was opened on, +1 for each in-app push.
 */
function readHistoryIdx(state: HistoryState): number {
  return typeof state?.idx === 'number' ? state.idx : 0;
}

function readEntryIdxStamp(state: HistoryState): number | null {
  const stamp = state?.[ENTRY_IDX_KEY];
  return typeof stamp === 'number' ? stamp : null;
}

export interface AppBack {
  /** The page has a back target (every AppLayout page but the dashboard). */
  canGoBack: boolean;
  goBack: () => void;
}

/**
 * The single back model of the app shell (header back button, edge swipe).
 *
 * Back returns to the page the user came from — history back past every entry
 * of the current page visit (its tab switches). A visit that started on the
 * first entry of the session (deep link, reload into a new tab, Telegram
 * startapp) has no in-app page behind it: back replaces it with the page's
 * hierarchical parent (`utils/backRoute`), which then falls back to its own
 * parent the same way — never a push, so back can never loop.
 *
 * Use once, in AppLayout: it tracks visits across all pages.
 */
export function useAppBack(): AppBack {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageKey, parent } = resolveBackRoute(location.pathname);
  const visitRef = useRef<{ pageKey: string; entryIdx: number } | null>(null);
  // A back already sent to history (`history.go` is async) — a double tap must
  // not step back twice from the same entry.
  const backPendingRef = useRef(false);

  // An entry's own stamp wins (re-entered via history); an unstamped entry of a
  // new page starts a new visit. Every entry of the visit gets the stamp.
  useLayoutEffect(() => {
    backPendingRef.current = false;
    const state = window.history.state as HistoryState;
    const stamp = readEntryIdxStamp(state);
    const entryIdx = stamp
      ?? (visitRef.current?.pageKey === pageKey ? visitRef.current.entryIdx : readHistoryIdx(state));
    visitRef.current = { pageKey, entryIdx };
    if (stamp !== entryIdx) {
      window.history.replaceState({ ...state, [ENTRY_IDX_KEY]: entryIdx }, '');
    }
  }, [location.key, pageKey]);

  const goBack = useCallback(() => {
    if (!parent || backPendingRef.current) return;
    const entryIdx = visitRef.current?.entryIdx ?? 0;
    const steps = readHistoryIdx(window.history.state as HistoryState) - entryIdx + 1;
    if (entryIdx > 0 && steps > 0) {
      backPendingRef.current = true;
      navigate(-steps);
    } else {
      navigate(parent, { replace: true });
    }
  }, [navigate, parent]);

  return { canGoBack: parent !== null, goBack };
}
